import { DashboardType } from '@eagle/core-data-types';
import {
  APP_BANNER_HEIGHT,
  APP_BAR_HEIGHT,
  DashboardAppBarProps,
  dashboardAppBarStyles,
  DashboardSwitcher,
  MicroPortal,
  SIDEBAR_FULL_WIDTH,
  SIDEBAR_WIDTH,
  useSidebarPinContext,
  useTitle
} from '@eagle/react-common';
import { AppBar, Breadcrumbs, Stack, styled, Typography } from '@ethings/ui-toolkit';
import { Link, Toolbar, useTheme } from '@mui/material';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useHref } from 'react-router-dom';

export const SPARE_FLEET_CAPACITY_FLAG = 'track-spare-fleet-capacity-feature';

const DashboardAppBar = styled(AppBar)<DashboardAppBarProps>(dashboardAppBarStyles);

const OuterStack = styled(Stack)({
  height: '100%',
  width: '100%',
  minWidth: 0,
  '.showAppBanner & .MuiAppBar-root': { marginTop: `${APP_BANNER_HEIGHT}px` },
});

const MainStack = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(3),
  flexShrink: 0,
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
  [theme.breakpoints.up('md')]: {
    display: 'flex',
  },
}));

const ContentBox = styled(Stack)(({ theme }) => ({
  flex: 1,
  width: '100%',
  minWidth: 0,
  minHeight: 0,
  overflow: 'auto',
  overflowX: 'hidden',
  padding: theme.spacing(3),
  [theme.breakpoints.down('md')]: {
    padding: 0,
  },
}));

export const SpareFleetCapacityReportPage: FC = () => {
  const { t } = useTranslation(['track']);
  const { isPinned } = useSidebarPinContext();
  const theme = useTheme();
  const href = useHref('/reports');
  useTitle(
    <DashboardSwitcher type={DashboardType.REPORT} sx={{ flex: 1, justifyItems: 'right', mr: -0.5 }}>
      <Typography noWrap variant="body1" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" mr={1} ml="auto">
        {t('track:page.reports.spare-fleet-capacity.title')}
      </Typography>
    </DashboardSwitcher>,
    true
  );

  return (
    <OuterStack>
      <MainStack>
        <DashboardAppBar
          height={APP_BAR_HEIGHT}
          isPinned={isPinned}
          sidebarFullWidth={SIDEBAR_FULL_WIDTH}
          sidebarWidth={SIDEBAR_WIDTH}
        >
          <Toolbar sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', width: 1 }}>
            <Breadcrumbs color={theme.palette.common.white}>
              <Link color={theme.palette.common.white} href={href}>
                {t('track:page.reports.title')}
              </Link>
              <DashboardSwitcher type={DashboardType.REPORT}>
                <Typography color={theme.palette.common.white}>
                  {t('track:page.reports.spare-fleet-capacity.title')}
                </Typography>
              </DashboardSwitcher>
            </Breadcrumbs>
          </Toolbar>
        </DashboardAppBar>
      </MainStack>
      <ContentBox>
        <MicroPortal
          app="spare-fleet-capacity"
          testId="spare-fleet-capacity-micro-portal"
          height={2000}
          autoResize
          scrolling={'yes'}
          errorFallback={() => (
            <Typography variant="body1" color={theme.palette.error.main}>
              {t('track:page.reports.spare-fleet-capacity.error')}
            </Typography>
          )}
        />
      </ContentBox>
    </OuterStack>
  );
};
